import { ChevronDown, CircleHelp, CircleSlash, Monitor, ShieldCheck, ShieldOff, TriangleAlert } from '@orgus/ui/icons';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { ControlMachineState, ControlTerminalFirst, Id } from '@orgus/protocol';
import { CONTROL_MIN_CLAUDE_VERSION, CONTROL_TERMINAL_WAIT_SECONDS } from '@orgus/protocol';
import { Button, DataState, ErrorState, IconButton, LiveBadge, Panel, Popover, PopoverContent, PopoverTrigger, Select, TONE_CLASS, Tooltip, cn, plural, toUiError, toast } from '@orgus/ui';
import { useConnection, useMachines } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { ControlConfirmDialogs } from './ControlSwitch.tsx';
import { setControl, useControl } from './live.ts';
import { WAIT_OPTIONS, machineIds, machineSummary, waitLabel } from './model.ts';

const SUMMARY_ICON = { passed: ShieldCheck, waiting: TriangleAlert, failed: CircleSlash } as const;

/** The one line under a machine's name: its Claude Code and its sessions, or what stops control. */
function shortLine(m: ControlMachineState | undefined, online: boolean, detail: string): string {
  const where = online ? '' : 'Offline · ';
  if (!m) return detail;
  if (m.installed && (m.problem || (!m.listening && online))) return detail;
  if (!m.support.ok) return `${where}Needs Claude Code ${CONTROL_MIN_CLAUDE_VERSION}`;
  const live = m.sessions.length;
  const controlled = m.sessions.filter((x) => x.controlled).length;
  const sessions = live === 0 ? 'no live sessions' : m.installed ? `${controlled} of ${plural(live, 'session')} controlled` : `${plural(live, 'live session')} watched`;
  return `${where}Claude Code ${m.support.version} · ${sessions}`;
}

function Fact({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className={cn('min-w-0 break-words text-ui text-fg-muted', mono && 'font-mono text-caption')}>{children}</dd>
    </div>
  );
}

/**
 * One machine on one line: name, its control state, the wait and Turn on or Turn off. The
 * Claude Code version, settings file and how requests wait sit behind the chevron; problems
 * stay visible because they change what you do next.
 */
function MachineRow({ id, name, online, m, canChange }: { id: Id; name: string; online: boolean; m: ControlMachineState | undefined; canChange: boolean }) {
  const [confirm, setConfirm] = useState<'on' | 'off' | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<ReturnType<typeof toUiError> | null>(null);
  const detailsId = useId();
  const s = machineSummary(m, online);
  const Icon = SUMMARY_ICON[s.tone as keyof typeof SUMMARY_ICON] ?? ShieldOff;
  const installed = !!m?.installed;
  const blocked = !canChange ? 'Only admins and the owner can change session control.' : !online ? `${name} is offline. Start Orgus there first.` : !installed && !s.canTurnOn ? s.detail : null;

  const change = async (on: boolean, waitSeconds?: number, terminalFirst?: ControlTerminalFirst) => {
    const r = await setControl(id, on, waitSeconds, terminalFirst);
    if (!r.ok) throw new Error(r.message ?? 'The machine did not change session control.');
    const done = terminalFirst && installed ? `Terminal sessions on ${name} ask ${terminalFirst === 'orgus' ? 'in Orgus' : 'in the terminal'} first` : waitSeconds !== undefined && installed ? `Wait set to ${waitLabel(waitSeconds)} on ${name}` : `Session control is on for ${name}`;
    toast(on ? done : `Session control is off for ${name}`, {
      tone: 'success',
      description: r.message,
    });
  };

  const changeWait = async (v: string) => {
    setBusy(true);
    setError(null);
    try {
      await change(true, Number(v));
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changeTerminalFirst = async (v: string) => {
    if (v !== 'orgus' && v !== 'terminal') return;
    setBusy(true);
    setError(null);
    try {
      await change(true, undefined, v);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sessions = m?.sessions ?? [];
  const notYet = sessions.filter((x) => !x.controlled);
  const problems = m ? [...(m.problem && installed ? [m.problem] : []), ...m.warnings] : [];
  // A machine that cannot run session control is a fact, not a failure: grey, like idle.
  const quiet = s.tone === 'neutral' || s.tone === 'idle' || (!!m && !m.support.ok);
  return (
    <li className="flex flex-col gap-2 px-4 py-3" aria-label={`${name}: ${s.label}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-1 basis-56 items-center gap-3">
          <Monitor className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <div className="min-w-0">
            <p className="flex min-w-0 items-center gap-2">
              <span className="truncate text-body font-medium text-fg">{name}</span>
              <span className={cn('inline-flex shrink-0 items-center gap-1 text-ui [&_svg]:size-3.5', quiet ? 'text-fg-subtle' : TONE_CLASS[s.tone].fg)}>
                <Icon aria-hidden />
                {s.label}
              </span>
            </p>
            <p className="text-caption text-fg-subtle md:truncate" title={s.detail}>
              {shortLine(m, online, s.detail)}
            </p>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {installed && (
            <Select
              size="sm"
              aria-label={`How long a permission request on ${name} waits for a person in Orgus`}
              options={WAIT_OPTIONS.some((o) => Number(o.value) === m!.waitSeconds) ? WAIT_OPTIONS : [...WAIT_OPTIONS, { value: String(m!.waitSeconds), label: waitLabel(m!.waitSeconds) }]}
              value={String(m!.waitSeconds)}
              disabled={!!blocked || busy}
              onValueChange={(v) => void changeWait(v)}
              className="text-caption"
            />
          )}
          <Tooltip content={blocked}>
            <span tabIndex={blocked ? 0 : -1}>
              {installed ? (
                <Button size="sm" icon={<ShieldOff />} disabled={!!blocked || busy} onClick={() => setConfirm('off')}>
                  Turn off
                </Button>
              ) : (
                <Button size="sm" variant="primary" icon={<ShieldCheck />} disabled={!!blocked || busy} onClick={() => setConfirm('on')}>
                  Turn on
                </Button>
              )}
            </span>
          </Tooltip>
          {m && (
            <IconButton
              size="sm"
              label={open ? `Hide details of ${name}` : `Show details of ${name}`}
              icon={<ChevronDown className={cn('transition-transform duration-(--duration-fast)', open && 'rotate-180')} />}
              aria-expanded={open}
              aria-controls={detailsId}
              onClick={() => setOpen((v) => !v)}
            />
          )}
        </div>
      </div>
      {open && m && (
        <dl id={detailsId} className="ml-7 grid gap-3 rounded-control bg-raised p-3 sm:grid-cols-2">
          <Fact label="Machine">{online ? 'Online' : 'Offline'}</Fact>
          <Fact label="Claude Code">{m.support.version ?? 'Not found'}</Fact>
          <Fact label="Settings file" mono>
            {m.settingsFile}
          </Fact>
          {installed ? (
            <Fact label="Requests wait">
              {waitLabel(m.waitSeconds)}, terminal sessions {m.terminalFirst === 'orgus' ? 'the same' : `${CONTROL_TERMINAL_WAIT_SECONDS} s`}, claude -p and SDK runs never
              {notYet.length > 0 && `. ${plural(notYet.length, 'live session')} ${notYet.length === 1 ? 'joins' : 'join'} at the next prompt`}
            </Fact>
          ) : (
            <Fact label="Now">{s.detail}</Fact>
          )}
          {installed && (
            <Fact label="Terminal sessions ask">
              <div className="flex">
                <Select
                  size="sm"
                  aria-label={`Where terminal sessions on ${name} ask first`}
                  options={[
                    { value: 'terminal', label: 'In the terminal first' },
                    { value: 'orgus', label: 'In Orgus first' },
                  ]}
                  value={m.terminalFirst ?? 'terminal'}
                  disabled={!!blocked || busy}
                  onValueChange={(v) => void changeTerminalFirst(v)}
                  className="text-caption"
                />
              </div>
            </Fact>
          )}
        </dl>
      )}
      {error && <ErrorState variant="inline" error={error} />}
      {problems.length > 0 && (
        <ul className="ml-7 flex flex-col gap-1" aria-label={`Problems on ${name}`}>
          {problems.map((w) => (
            <li key={w} className="flex items-start gap-1.5 text-sm text-waiting">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
      <ControlConfirmDialogs name={name} m={m} confirm={confirm} onClose={() => setConfirm(null)} change={change} />
    </li>
  );
}

/** How control works, on demand: a help popover beside the panel's live state. */
function HowItWorks() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton size="sm" label="How session control works" icon={<CircleHelp />} />
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 text-ui text-fg-muted" aria-label="How session control works">
        Hooks call the machine's agent over http, so nothing opens a window. Orgus answers budget stops and its rules at once and puts permission requests in the Inbox. Off by
        default, removed with one switch, and fail safe when Orgus is down.
      </PopoverContent>
    </Popover>
  );
}

/** Settings, Session control: one row per machine. */
export default function ControlSettings() {
  const view = useControl();
  const machines = useMachines();
  const { connection } = useConnection();
  const canChange = useCan('admin');
  const ids = machineIds(view.state, machines.map((m) => m.id));
  const byId = new Map(machines.map((m) => [m.id, m]));
  const noReport = ids.filter((id) => !view.state?.machines[id] && byId.get(id)?.online).length;

  const state =
    view.phase === 'loading' ? 'loading' : view.phase === 'error' ? 'error' : view.phase === 'missing' ? 'empty' : ids.length === 0 ? 'empty' : noReport > 0 ? 'partial' : 'full';

  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Control every session"
        sub={ids.length || undefined}
        actions={
          <span className="flex items-center gap-2">
            {view.phase === 'ready' && <LiveBadge mode={view.mock || connection === 'mock' ? 'paused' : connection === 'live' ? 'live' : connection === 'offline' || connection === 'hub-error' || connection === 'signed-out' ? 'offline' : 'reconnecting'} />}
            <HowItWorks />
          </span>
        }
        flush
      >
        <DataState
          state={state}
          loadingLabel="Loading session control"
          error={{ what: 'Session control could not be loaded.', why: 'The hub did not send its state.', next: 'Check that Orgus is running, then reload.' }}
          empty={
            view.phase === 'missing'
              ? { icon: <ShieldOff />, title: 'This hub has no session control', description: 'Update Orgus on the hub machine.' }
              : { icon: <Monitor />, title: 'No machines yet', description: 'Add a machine to set session control.', action: <Link to="/machines">Go to Machines</Link> }
          }
          partial={`${noReport} online machine${noReport === 1 ? '' : 's'} did not report session control: ${noReport === 1 ? 'its agent is' : 'their agents are'} older than this hub.`}
        >
          <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Machines">
            {ids.map((id) => {
              const mc = byId.get(id);
              return <MachineRow key={id} id={id} name={mc?.name ?? id} online={mc?.online ?? view.state?.machines[id]?.online ?? false} m={view.state?.machines[id]} canChange={canChange} />;
            })}
          </ul>
        </DataState>
      </Panel>
    </div>
  );
}
